import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './AuditTrail.scss';

class AuditTrail extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            auditLogs: [],
            totalRecords: 0,
            lazyParams: {
                first: 0,
                rows: 20,
                page: 1
            },
            filters: {
                eventType: 'ALL',
                startDate: null,
                endDate: null,
                searchText: ''
            }
        };

        this.eventTypeOptions = [
            { label: 'All Events', value: 'ALL' },
            { label: 'Risk Adjusted', value: 'RISK_ADJUSTED' },
            { label: 'Promise Kept', value: 'PROMISE_KEPT' },
            { label: 'Promise Breached', value: 'PROMISE_BREACHED' },
            { label: 'Payment Received', value: 'PAYMENT_RECEIVED' },
            { label: 'Call Outcome', value: 'CALL_OUTCOME_LOGGED' },
            { label: 'Risk Band Changed', value: 'RISK_BAND_CHANGED' },
            { label: 'Action Completed', value: 'ACTION_COMPLETED' },
            { label: 'System Calibration', value: 'SYSTEM_CALIBRATION' }
        ];
    }

    componentDidMount() {
        this.fetchAuditTrail();
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    fetchAuditTrail = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');
        const { filters, lazyParams } = this.state;

        if (!academicYear) {
            this.showToast('error', 'Error', 'Missing academic year');
            return;
        }

        this.setState({ loading: true });

        try {
            let url = `${baseUrlAdmin}/fee-decision/governance/audit-trail?academicYear=${academicYear}&page=${lazyParams.page}&limit=${lazyParams.rows}`;
            
            if (filters.eventType && filters.eventType !== 'ALL') {
                url += `&eventType=${filters.eventType}`;
            }

            if (filters.startDate) {
                url += `&startDate=${filters.startDate.toISOString()}`;
            }

            if (filters.endDate) {
                url += `&endDate=${filters.endDate.toISOString()}`;
            }

            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    auditLogs: response.res.data || [],
                    totalRecords: response.res.pagination?.total || 0
                });
            } else {
                this.showToast('error', 'Error', 'Failed to fetch audit trail');
            }
        } catch (error) {
            console.error('Error fetching audit trail:', error);
            this.showToast('error', 'Error', 'Failed to fetch audit trail');
        } finally {
            this.setState({ loading: false });
        }
    }

    onPage = (event) => {
        this.setState({
            lazyParams: { ...this.state.lazyParams, first: event.first, rows: event.rows, page: event.page + 1 }
        }, this.fetchAuditTrail);
    }

    handleFilterChange = () => {
        this.setState({
            lazyParams: { ...this.state.lazyParams, first: 0, page: 1 }
        }, this.fetchAuditTrail);
    }

    handleClearFilters = () => {
        this.setState({
            filters: {
                eventType: 'ALL',
                startDate: null,
                endDate: null,
                searchText: ''
            },
            lazyParams: { ...this.state.lazyParams, first: 0, page: 1 }
        }, this.fetchAuditTrail);
    }

    // Body Templates
    dateBodyTemplate = (rowData) => {
        if (!rowData.createdAt) return 'N/A';
        
        const date = new Date(rowData.createdAt);
        return (
            <div>
                <div className="date">{date.toLocaleDateString('en-GB')}</div>
                <div className="time">{date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
        );
    }

    getRiskBandTag = (riskBand) => {
        if (!riskBand) return null;
        const band = riskBand?.toUpperCase();
        let severity = null;
        let style = null;

        switch (band) {
            case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
            case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C25400' }; break; // Explicit Dark Orange
            case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
            case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
            default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }
        return <Tag value={riskBand} severity={severity} style={style} />;
    }

    studentNameBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="student-name">{rowData.studentName || 'N/A'}</div>
                <div className="student-id">{rowData.admissionNo || rowData.studentId}</div>
            </div>
        );
    }

    eventTypeBodyTemplate = (rowData) => {
        const severityMap = {
            'PROMISE_KEPT': 'success',
            'PAYMENT_RECEIVED': 'success',
            'PROMISE_BREACHED': 'danger',
            'RISK_ADJUSTED': 'info',
            'CALL_OUTCOME_LOGGED': 'warning',
            'RISK_BAND_CHANGED': 'danger',
            'ACTION_COMPLETED': 'success',
            'SYSTEM_CALIBRATION': 'info'
        };

        const labelMap = {
            'PROMISE_KEPT': 'Promise Kept',
            'PAYMENT_RECEIVED': 'Payment',
            'PROMISE_BREACHED': 'Promise Breached',
            'RISK_ADJUSTED': 'Risk Adjusted',
            'CALL_OUTCOME_LOGGED': 'Call Logged',
            'RISK_BAND_CHANGED': 'Band Changed',
            'ACTION_COMPLETED': 'Action Done',
            'SYSTEM_CALIBRATION': 'System Event'
        };

        return (
            <Tag 
                value={labelMap[rowData.eventType] || rowData.eventType} 
                severity={severityMap[rowData.eventType] || 'info'}
            />
        );
    }

    riskChangeBodyTemplate = (rowData) => {
        if (!rowData.riskScoreChange && rowData.riskScoreChange !== 0) return '-';

        const change = rowData.riskScoreChange;
        const isIncrease = change > 0;
        
        return (
            <div className={`risk-change ${isIncrease ? 'increase' : 'decrease'}`}>
                <i className={`pi ${isIncrease ? 'pi-arrow-up' : 'pi-arrow-down'}`} />
                <span>{Math.abs(change)}</span>
            </div>
        );
    }

    performedByBodyTemplate = (rowData) => {
        const isAutomated = rowData.automated !== false;
        const performedBy = rowData.performedByName || rowData.performedBy || 'System';
        
        return (
            <div>
                <div className="performed-by">{performedBy}</div>
                {isAutomated && <Tag value="Auto" severity="info" className="p-tag-sm" />}
            </div>
        );
    }

    expandedRowTemplate = (rowData) => {
        return (
            <div className="expanded-row">
                <div className="expanded-section">
                    <h4>Event Details</h4>
                    <div className="detail-grid">
                        {rowData.previousRiskScore !== undefined && (
                            <div className="detail-item">
                                <span className="label">Previous Risk Score:</span>
                                <span className="value">{rowData.previousRiskScore}</span>
                            </div>
                        )}
                        {rowData.newRiskScore !== undefined && (
                            <div className="detail-item">
                                <span className="label">New Risk Score:</span>
                                <span className="value">{rowData.newRiskScore}</span>
                            </div>
                        )}
                        {rowData.previousRiskBand && (
                            <div className="detail-item">
                                <span className="label">Previous Risk Band:</span>
                                {this.getRiskBandTag(rowData.previousRiskBand)}
                            </div>
                        )}
                        {rowData.newRiskBand && (
                            <div className="detail-item">
                                <span className="label">New Risk Band:</span>
                                {this.getRiskBandTag(rowData.newRiskBand)}
                            </div>
                        )}
                        {rowData.triggerType && (
                            <div className="detail-item">
                                <span className="label">Trigger Type:</span>
                                <span className="value">{rowData.triggerType}</span>
                            </div>
                        )}
                    </div>
                </div>

                {rowData.ruleApplied && (
                    <div className="expanded-section">
                        <h4>Rule Applied</h4>
                        <div className="rule-info">
                            <div><strong>Rule:</strong> {rowData.ruleApplied.ruleName}</div>
                            <div><strong>Description:</strong> {rowData.ruleApplied.ruleDescription}</div>
                            <div><strong>Adjustment:</strong> {rowData.ruleApplied.adjustmentValue}</div>
                        </div>
                    </div>
                )}

                {rowData.triggerDetails && Object.keys(rowData.triggerDetails).length > 0 && (
                    <div className="expanded-section">
                        <h4>Additional Context</h4>
                        <pre>{JSON.stringify(rowData.triggerDetails, null, 2)}</pre>
                    </div>
                )}
            </div>
        );
    }

    // Filter displayed audit logs based on search text
    getFilteredAuditLogs = () => {
        const { auditLogs, filters } = this.state;
        const searchText = filters.searchText.toLowerCase();

        if (!searchText) return auditLogs;

        return auditLogs.filter(log => 
            (log.studentName && log.studentName.toLowerCase().includes(searchText)) ||
            (log.admissionNo && log.admissionNo.toLowerCase().includes(searchText)) ||
            (log.studentId && log.studentId.toLowerCase().includes(searchText)) ||
            (log.message && log.message.toLowerCase().includes(searchText))
        );
    }

    render() {
        const { loading, totalRecords, lazyParams, filters } = this.state;
        const filteredAuditLogs = this.getFilteredAuditLogs();

        if (loading && filteredAuditLogs.length === 0) {
            return <LoadingComponent />;
        }

        return (
            <div className="audit-trail">
                <Toast ref={this.toast} position="bottom-right" />
                
                <Card className="header-card">
                    <div className="header-content">
                        <div className="header-left">
                            <h2>🗂️ Audit Trail</h2>
                            <p className="description-text">
                                Complete audit log of all system events, user actions, and risk adjustments. 
                                Use filters to narrow down events by type, date range, or student.
                            </p>
                        </div>
                        <div className="header-stats">
                            <div className="stat-item">
                                <span className="stat-label">Total Events</span>
                                <span className="stat-value">{totalRecords}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Current Page</span>
                                <span className="stat-value">{lazyParams.page}</span>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="filters-card">
                    <div className="filters-section">
                        <div className="filter-row">
                            <div className="filter-item">
                                <label>Search</label>
                                <InputText
                                    value={filters.searchText}
                                    onChange={(e) => this.setState({ 
                                        filters: { ...filters, searchText: e.target.value } 
                                    })}
                                    placeholder="Student name, ID, or message..."
                                    className="p-inputtext-sm"
                                />
                            </div>
                            <div className="filter-item">
                                <label>Event Type</label>
                                <Dropdown
                                    value={filters.eventType}
                                    options={this.eventTypeOptions}
                                    onChange={(e) => this.setState({ 
                                        filters: { ...filters, eventType: e.value } 
                                    })}
                                    placeholder="All Events"
                                />
                            </div>
                            <div className="filter-item">
                                <label>Start Date</label>
                                <Calendar
                                    value={filters.startDate}
                                    onChange={(e) => this.setState({ 
                                        filters: { ...filters, startDate: e.value } 
                                    })}
                                    placeholder="From date"
                                    showIcon
                                    dateFormat="dd/mm/yy"
                                />
                            </div>
                            <div className="filter-item">
                                <label>End Date</label>
                                <Calendar
                                    value={filters.endDate}
                                    onChange={(e) => this.setState({ 
                                        filters: { ...filters, endDate: e.value } 
                                    })}
                                    placeholder="To date"
                                    showIcon
                                    dateFormat="dd/mm/yy"
                                />
                            </div>
                        </div>
                        <div className="filter-actions">
                            <Button 
                                label="Apply Filters" 
                                icon="pi pi-filter" 
                                onClick={this.handleFilterChange}
                                loading={loading}
                            />
                            <Button 
                                label="Clear" 
                                icon="pi pi-filter-slash" 
                                className="p-button-outlined"
                                onClick={this.handleClearFilters}
                            />
                            <Button 
                                label="Refresh" 
                                icon="pi pi-refresh" 
                                className="p-button-outlined"
                                onClick={this.fetchAuditTrail}
                                loading={loading}
                            />
                        </div>
                    </div>
                </Card>

                <Card className="table-card">
                    <DataTable
                        value={filteredAuditLogs}
                        loading={loading}
                        lazy
                        paginator
                        first={lazyParams.first}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={this.onPage}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        emptyMessage="No audit logs found"
                        className="audit-table"
                        stripedRows
                        responsiveLayout="scroll"
                        expandedRows={[]}
                        onRowToggle={(e) => this.setState({ expandedRows: e.data })}
                        rowExpansionTemplate={this.expandedRowTemplate}
                        dataKey="_id"
                    >
                        <Column expander style={{ width: '3rem' }} />
                        <Column 
                            header="Date & Time" 
                            body={this.dateBodyTemplate}
                            sortable
                            sortField="createdAt"
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            header="Student" 
                            body={this.studentNameBodyTemplate}
                            style={{ minWidth: '180px' }}
                        />
                        <Column 
                            field="className" 
                            header="Class"
                            style={{ minWidth: '100px' }}
                        />
                        <Column 
                            header="Event Type" 
                            body={this.eventTypeBodyTemplate}
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            field="message" 
                            header="Description"
                            style={{ minWidth: '300px' }}
                        />
                        <Column 
                            header="Risk Change" 
                            body={this.riskChangeBodyTemplate}
                            sortable
                            sortField="riskScoreChange"
                            style={{ minWidth: '120px' }}
                        />
                        <Column 
                            header="Performed By" 
                            body={this.performedByBodyTemplate}
                            style={{ minWidth: '150px' }}
                        />
                    </DataTable>
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.currentUserPerms?.userInfo
});

export default connect(mapStateToProps)(withRouter(AuditTrail));
